import type {
    ReactElement,
    ReactNode,
} from "preact/compat";
import {
    useRef,
    useState,
} from "preact/compat";

type TooltipProps = {
    content: string;
    children: ReactNode;
    position?: "top" | "bottom" | "left" | "right";
    delay?: number;
    fullWidth?: boolean;
};

export const Tooltip = ({
    content,
    children,
    position = "top",
    delay = 400,
    fullWidth = false,
}: TooltipProps): ReactElement => {
    const [visible, setVisible] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const show = (): void => {
        timeoutRef.current = setTimeout(() => setVisible(true), delay);
    };

    const hide = (): void => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setVisible(false);
    };

    const positionClasses: Record<string, string> = {
        top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
        bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
        left: "right-full top-1/2 -translate-y-1/2 mr-2",
        right: "left-full top-1/2 -translate-y-1/2 ml-2",
    };

    return (
        <div
            className={`relative ${fullWidth ? "flex w-full" : "inline-flex"}`}
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            {children}
            {visible && (
                <div
                    className={`absolute z-50 px-2.5 py-1.5 text-xs text-text-primary bg-bg-tertiary border border-border rounded-md shadow-lg whitespace-nowrap pointer-events-none ${
                        positionClasses[position]
                    }`}
                >
                    {content}
                </div>
            )}
        </div>
    );
};
